import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import HypeHafenLogo from "./HypeHafenLogo";

export default function LegalPage({ title, eyebrow, children }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#2E2E2E]" data-testid="legal-page">
      {/* Slim dark header */}
      <header className="bg-[#2E2E2E] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="legal-logo-button"
            className="flex items-center"
            aria-label="Zur Startseite"
          >
            <HypeHafenLogo height={56} />
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="legal-back-button"
            className="inline-flex items-center gap-2 font-body text-sm text-white/80 hover:text-[#FFF44F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zur Startseite
          </button>
        </div>
      </header>

      {/* Hero strip */}
      <section className="bg-[#FBFBF9] border-b border-[#ECECEC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
          <div className="hh-eyebrow mb-4">{eyebrow}</div>
          <h1 className="hh-headline text-[2.4rem] sm:text-[3rem] lg:text-[3.6rem] text-[#2E2E2E]">
            {title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20 font-body text-[#2E2E2E] leading-relaxed">
        <div className="legal-prose">{children}</div>

        <div className="mt-16 pt-8 border-t border-[#ECECEC]">
          <button
            onClick={() => navigate("/")}
            data-testid="legal-back-bottom-button"
            className="hh-btn-dark"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
        </div>
      </main>

      {/* Compact footer */}
      <footer className="bg-[#2E2E2E] text-white/60 text-sm py-8">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>© {new Date().getFullYear()} Hype Hafen. Aufmerksamkeit. Strategie. Wachstum.</div>
          <div className="flex gap-6 font-body">
            <a href="/impressum" className="hover:text-[#FFF44F]" data-testid="footer-mini-impressum">Impressum</a>
            <a href="/datenschutz" className="hover:text-[#FFF44F]" data-testid="footer-mini-datenschutz">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* Tiny helper components for consistent prose styling */
export const LegalH2 = ({ children }) => (
  <h2 className="font-display text-[#2E2E2E] text-xl sm:text-2xl mt-10 mb-3">{children}</h2>
);

export const LegalH3 = ({ children }) => (
  <h3 className="font-display text-[#2E2E2E] text-base sm:text-lg mt-6 mb-2">{children}</h3>
);

export const LegalP = ({ children }) => (
  <p className="font-body text-[#2E2E2E]/85 text-[0.97rem] leading-[1.7] mb-3">{children}</p>
);

export const LegalUL = ({ items }) => (
  <ul className="font-body text-[#2E2E2E]/85 text-[0.97rem] leading-[1.7] list-disc pl-6 mb-3 space-y-1">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

/* Placeholder pill */
export const Placeholder = ({ children }) => (
  <span
    className="inline-block bg-[#FFF44F]/40 border border-dashed border-[#2E2E2E]/40 rounded px-2 py-0.5 font-body text-[0.85rem] text-[#2E2E2E]"
    data-testid="legal-placeholder"
  >
    [ {children} ]
  </span>
);
